import React, { useCallback, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';

import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';

import TabPanelShell from '~/components/Offers/Detail/OfferBuilder/shared/TabPanelShell';

import { useGetAccountAssets } from '~/hooks/apis/offers/useGetAccountAssets';
import { useGetOffer } from '~/hooks/apis/offers/useGetOffer';

import { uniqueBy } from '~/utils/arrayUtils';

import {
  usePackageOptionsContext,
  usePropertySetupContext,
  useScrapeSessionContext,
} from '../../PricingToolSessionContext';
import { PRICING_OPTION_LABELS } from '../../constants';
import RawCsvDataTable from '../scrape/RawCsvDataTable';
import { computeDailyDiscountStats } from '../scrape/otaParity/computeGenuineDiscount';
import { computeDailyMarketStats } from '../scrape/otaParity/computeOtaMarketStats';

import CopyFromOptionBanner from './CopyFromOptionBanner';
import LinkRoomToAssetDialog from './LinkRoomToAssetDialog';
import MarginTable from './MarginTable';
import NetRateFormulaDialog from './NetRateFormulaDialog';
import OtaParitySummaryTable from './OtaParitySummaryTable';
import PackageUpgradesCard from './PackageUpgradesCard';
import PastePreviousOfferDialog from './PastePreviousOfferDialog';
import PricingTable from './PricingTable';
import { BASIC_CATEGORY_INDEX, MAX_LOS, MIN_LOS } from './packageCategories';
import { DEFAULT_NET_RATE_FORMULA_STATE, collectRoomAssets, takenRoomAssetKeys } from './packageOptionsModel';
import type { PackageOptionId } from './types';

type PackageOptionsTabProps = {
  offerId?: string;
};

export default function PackageOptionsTab({ offerId }: PackageOptionsTabProps) {
  const { enqueueSnackbar } = useSnackbar();
  const { parsedCsv, stayInputs } = useScrapeSessionContext();
  const propertySetup = usePropertySetupContext();
  const {
    currency: netCurrency,
    rate: audToNetRate,
    audToSourceCurrencyRate,
    isLoading: isNetRateLoading,
  } = propertySetup.fx;
  const { data: offer, error: offerError } = useGetOffer(offerId);
  const { data: accountRoomAssets, error: accountRoomAssetsError } = useGetAccountAssets(
    offer?.vendor_account_id,
    'rooms',
  );
  const [otaComparisonOpen, setOtaComparisonOpen] = useState(false);
  const marketStats = useMemo(
    () => (parsedCsv ? computeDailyMarketStats(parsedCsv, undefined, stayInputs?.blackoutDates) : []),
    [parsedCsv, stayInputs?.blackoutDates],
  );
  const discountStats = useMemo(
    () => (stayInputs ? computeDailyDiscountStats(marketStats, stayInputs) : []),
    [marketStats, stayInputs],
  );

  const {
    activeOption,
    setActiveOption,
    optionState,
    roomAssets,
    addRoomAsset,
    addRoomType,
    copyFromFirstOption,
    deleteRoomType,
    selectAsset,
    toggleAsiaOnly,
    updateRoomPricing,
    setRoomSellPrice,
    setRoomNetRate,
    setNetRateFormula,
    applyNetRateFormula,
    applyPastedOffer,
    duplicateAsUpgrade,
    duplicateAsSolo,
    setSelectedLos,
    addLos,
    deleteLos,
  } = usePackageOptionsContext();
  const { lengthsOfStay, roomsByCategory, selectedLos, pricingByRoomId, netRateFormula } = optionState;

  const [newLos, setNewLos] = useState('');
  const [netRateDialogOpen, setNetRateDialogOpen] = useState(false);
  const [pasteOfferDialogOpen, setPasteOfferDialogOpen] = useState(false);
  const [linkAssetDialogOpen, setLinkAssetDialogOpen] = useState(false);

  const allAssets = useMemo(
    () => collectRoomAssets(accountRoomAssets ?? [], roomAssets),
    [accountRoomAssets, roomAssets],
  );

  const takenAssetKeys = useMemo(() => takenRoomAssetKeys(roomsByCategory[BASIC_CATEGORY_INDEX]), [roomsByCategory]);

  const baseRooms = useMemo(
    () =>
      uniqueBy(
        roomsByCategory[BASIC_CATEGORY_INDEX].filter((room) => room.assetId),
        (room) => room.assetId,
      ),
    [roomsByCategory],
  );
  const baseRoomAssetIds = useMemo(() => baseRooms.map((room) => room.assetId), [baseRooms]);
  const baseRoomLabel = useCallback(
    (assetId: string) => {
      const room = baseRooms.find((candidate) => candidate.assetId === assetId);
      return room?.assetName ?? assetId;
    },
    [baseRooms],
  );

  const handleSetRoomSellPrice = useCallback(
    (id: PackageOptionId, defaultOccupancy: string, nights: number, value: string) =>
      setRoomSellPrice(id, defaultOccupancy, nights, value, audToNetRate ?? 1),
    [setRoomSellPrice, audToNetRate],
  );

  const netRateRooms = useMemo(
    () =>
      roomsByCategory
        .flat()
        .filter((room) => room.assetName)
        .map((room) => ({ id: room.id, name: room.packageName })),
    [roomsByCategory],
  );
  // "All room types" covers unlinked rows too; only the "choose" checklist needs an asset name.
  const allRoomIds = useMemo(() => roomsByCategory.flat().map((room) => room.id), [roomsByCategory]);

  function handleAddLos() {
    addLos(Number(newLos), audToNetRate ?? 1);
    setNewLos('');
  }

  return (
    <TabPanelShell
      subtitle="Configure room types, LOS, pricing, and package upgrades across options."
      title="Package Options"
    >
      <Paper variant="outlined">
        <OtaParitySummaryTable />
      </Paper>

      <Box display="flex" justifyContent="flex-end" mt={2}>
        <Button disabled={!parsedCsv || !stayInputs} onClick={() => setOtaComparisonOpen(true)} variant="contained">
          OTA Comparison
        </Button>
      </Box>

      {parsedCsv && stayInputs && (
        <RawCsvDataTable
          currencyCode={stayInputs.currencyCode}
          discountStats={discountStats}
          marketStats={marketStats}
          onClose={() => setOtaComparisonOpen(false)}
          open={otaComparisonOpen}
          parsed={parsedCsv}
        />
      )}

      <Paper sx={{ mt: 4, p: 2 }} variant="outlined">
        <Stack spacing={2}>
          <Typography fontWeight="bold" variant="h6">
            Package Options
          </Typography>

          {(offerError || accountRoomAssetsError) && (
            <Alert severity="error">Unable to load account room assets. Only assets created here are listed.</Alert>
          )}

          <Box>
            <Typography color="text.secondary" variant="caption" sx={{ display: 'block', mb: 0.75 }}>
              Room assets
            </Typography>
            <Stack direction="row" flexWrap="wrap" spacing={1} useFlexGap>
              {allAssets.filter((asset) => asset.name).length === 0 ? (
                <Typography color="text.secondary" fontStyle="italic" variant="caption">
                  No assets yet — created when adding a room type
                </Typography>
              ) : (
                allAssets
                  .filter((asset) => asset.name)
                  .map((asset) => <Chip key={asset.id} label={asset.name} size="small" />)
              )}
            </Stack>
          </Box>

          <Divider />

          <ToggleButtonGroup
            exclusive
            onChange={(_event, next) => next !== null && setActiveOption(next)}
            size="small"
            value={activeOption}
          >
            {PRICING_OPTION_LABELS.map((label, index) => (
              <ToggleButton key={index} value={index}>
                {label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          {activeOption !== 0 && (
            <CopyFromOptionBanner
              onCopy={copyFromFirstOption}
              sourceOptionLabel={PRICING_OPTION_LABELS[0]}
              targetOptionLabel={PRICING_OPTION_LABELS[activeOption]}
            />
          )}

          <Stack alignItems="center" direction="row" flexWrap="wrap" spacing={2}>
            <Tooltip title={isNetRateLoading ? `Waiting for the ${netCurrency} exchange rate to load…` : ''}>
              <span>
                <Button
                  disabled={isNetRateLoading}
                  onClick={() => {
                    setNetRateFormula(DEFAULT_NET_RATE_FORMULA_STATE);
                    setNetRateDialogOpen(true);
                  }}
                  size="small"
                  variant="text"
                >
                  Net Rate formula
                </Button>
              </span>
            </Tooltip>
          </Stack>

          <NetRateFormulaDialog
            allRoomIds={allRoomIds}
            formula={netRateFormula}
            onApply={({ applyToExtraNight, netPercent, roomIds }) => {
              if (audToNetRate === undefined) {
                enqueueSnackbar(`Exchange rate for ${netCurrency} unavailable — net rate not applied`, {
                  variant: 'error',
                });
                return;
              }
              applyNetRateFormula(netPercent, applyToExtraNight, audToNetRate, roomIds);
            }}
            onChangeFormula={setNetRateFormula}
            onClose={() => setNetRateDialogOpen(false)}
            open={netRateDialogOpen}
            rooms={netRateRooms}
          />

          <Stack alignItems="center" direction="row" flexWrap="wrap" spacing={1}>
            <Typography color="text.secondary" variant="body2">
              Lengths of stay:
            </Typography>
            {lengthsOfStay.map((nights) => (
              <Chip
                key={nights}
                label={`${nights}N`}
                onDelete={lengthsOfStay.length > 1 ? () => deleteLos(nights) : undefined}
                size="small"
              />
            ))}
            <TextField
              inputProps={{ max: MAX_LOS, min: MIN_LOS }}
              onChange={(e) => setNewLos(e.target.value.replace(/\D/g, '').slice(0, 3))}
              placeholder="night:"
              size="small"
              sx={{ width: 90 }}
              type="number"
              value={newLos}
            />
            <Button disabled={!newLos} onClick={handleAddLos} size="small" variant="outlined">
              + Add LOS
            </Button>
          </Stack>

          <PricingTable
            assets={allAssets}
            netCurrency={netCurrency}
            onDeleteRoomType={deleteRoomType}
            onSelectAsset={selectAsset}
            onSelectedLosChange={setSelectedLos}
            onToggleAsiaOnly={(categoryIndex, id) => {
              const room = roomsByCategory[categoryIndex]?.find((candidate) => candidate.id === id);
              const enabling = room !== undefined && !room.asiaOnly;
              const refusal = toggleAsiaOnly(categoryIndex, id);
              if (refusal) {
                enqueueSnackbar(refusal, { variant: 'warning' });
              } else if (enabling) {
                enqueueSnackbar(`Asia only enabled for ${room.assetName}`, { variant: 'info' });
              } else if (room) {
                enqueueSnackbar(`Asia only disabled for ${room.assetName}`, { variant: 'info' });
              }
            }}
            onUpdateRoomPricing={updateRoomPricing}
            onSetRoomSellPrice={handleSetRoomSellPrice}
            onSetRoomNetRate={setRoomNetRate}
            pricingByRoomId={pricingByRoomId}
            roomsByCategory={roomsByCategory}
            selectedLos={selectedLos}
            sortedLos={lengthsOfStay}
          />

          <MarginTable
            audToSourceCurrencyRate={audToSourceCurrencyRate}
            netCurrency={netCurrency}
            pricingByRoomId={pricingByRoomId}
            roomsByCategory={roomsByCategory}
            sortedLos={lengthsOfStay}
          />

          <Stack direction="row" spacing={2}>
            <Button onClick={() => setLinkAssetDialogOpen(true)} startIcon="+" variant="outlined">
              Add room type
            </Button>
            <Button onClick={() => setPasteOfferDialogOpen(true)} variant="outlined">
              Paste previous offer
            </Button>
          </Stack>

          <LinkRoomToAssetDialog
            assets={allAssets}
            onAddRoom={(asset) => addRoomType(BASIC_CATEGORY_INDEX, asset)}
            onClose={() => setLinkAssetDialogOpen(false)}
            onCreateAsset={addRoomAsset}
            open={linkAssetDialogOpen}
            takenAssetKeys={takenAssetKeys}
          />

          <PastePreviousOfferDialog
            assets={allAssets}
            linkedAssetIds={baseRoomAssetIds}
            onApply={(roomRows) => applyPastedOffer(roomRows, allAssets)}
            onClose={() => setPasteOfferDialogOpen(false)}
            open={pasteOfferDialogOpen}
          />
        </Stack>
      </Paper>

      <PackageUpgradesCard
        audToNetRate={audToNetRate ?? 1}
        baseRoomAssetIds={baseRoomAssetIds}
        baseRoomLabel={baseRoomLabel}
        netCurrency={netCurrency}
        onDuplicateAsSolo={duplicateAsSolo}
        onDuplicateAsUpgrade={duplicateAsUpgrade}
        optionLabels={PRICING_OPTION_LABELS}
      />
    </TabPanelShell>
  );
}
